import { ChevronDown, ChevronUp } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { badgeClasses, convertISODateToMMDDYYYY, formatCurrency } from '@/lib/utils';
import { getCheckRegister, useCheckRegisterFilterOptions, useUpdateCheckStatus } from '@/services/checkRegisterService';
import { CheckRegisterItem, CheckRegisterQueryParams } from '@/types/checkRegister.types';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { WorkOrderPaymentStatus } from '@/types/workOrder.types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { hasFeature } from '@/services/featureService';
import AddCheckNumberModal from '@/components/AddCheckNumberModal';

const statusBadges: Record<string, { label: string; bgColor: string; fontColor: string; hoverColor: string }> = {
  REQUESTED: {
    label: 'Requested',
    bgColor: '#FFEEBF',
    fontColor: '#6C4A09',
    hoverColor: '#FFEEBF',
  },
  BILLED: {
    label: 'Issued',
    bgColor: '#D5E6FB',
    fontColor: '#1551B2',
    hoverColor: '#D5E6FB',
  },
  CASHED: {
    label: 'Cashed',
    bgColor: '#D5F4E3',
    fontColor: '#1B8652',
    hoverColor: '#D5F4E3',
  },
  VOID: {
    label: 'Void',
    bgColor: '#FFE0E0',
    fontColor: '#A41919',
    hoverColor: '#FFE0E0',
  },
};

const CheckRegister = () => {
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [tableData, setTableData] = useState<{
    data: CheckRegisterItem[];
    rest?: unknown;
  }>();
  const [selectAll, setSelectAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAddCheckModalOpen, setIsAddCheckModalOpen] = useState(false);
  const [selectedPaymentForAddCheck, setSelectedPaymentForAddCheck] = useState<CheckRegisterItem | null>(null);
  const [openSecondaryColumns, setOpenSecondaryColumns] = useState<{ id: string; isExpanded: boolean }[]>([]);
  const filterOptionsData = useCheckRegisterFilterOptions();
  const hasCheckUpdateFeature = hasFeature('accounting.checkRegister.update');
  const { setIsDirty } = useContext(NavigationBlockerContext);
  useEffect(() => {
    setIsDirty(false);
  }, []);
  const updateStatusMutation = useUpdateCheckStatus();
  const hasBulkUpdatePermission = hasFeature('accounting.checkRegister.updateStatus');

  const { clientOptions, invoiceOptions, checkNumberOptions, fileNumberOptions } = useMemo(() => {
    return {
      clientOptions: filterOptionsData.data?.clients ?? [],
      invoiceOptions: filterOptionsData.data?.invoiceNumbers ?? [],
      checkNumberOptions: filterOptionsData.data?.checkNumbers ?? [],
      fileNumberOptions: filterOptionsData.data?.fileNumbers ?? [],
    };
  }, [filterOptionsData.data]);

  const [summaryData, setSummaryData] = useState<{
    totalAmount?: string;
    totalChecks?: number;
  }>({});

  const apiGetWrapper = useCallback((params: Record<string, unknown>) => {
    const apiParams: CheckRegisterQueryParams = {
      ...params,
      pageNo: params.pageNo as number,
      pageSize: params.pageSize as number,
    };
    const result = getCheckRegister(apiParams);

    return {
      data: result.data,
      totalRows: result.totalRows,
      isFetching: result.isFetching,
      totalAmount: result.totalAmount,
      totalChecks: result.totalChecks,
    };
  }, []);

  const handleCheckboxChange = (paymentId: string, isChecked: string | boolean) => {
    if (isChecked) {
      setSelectedPayments((prev) => [...prev, paymentId]);
    } else {
      setSelectedPayments((prev) => prev.filter((id) => id !== paymentId));
    }
  };

  const handleSelectAllChange = (isChecked: boolean) => {
    setSelectAll(isChecked);
    if (isChecked) {
      const eligibleIds =
        tableData?.data
          ?.filter(
            (row) => row.status === WorkOrderPaymentStatus.BILLED || row.status === WorkOrderPaymentStatus.REQUESTED
          )
          .map((row) => row.paymentId) ?? [];
      setSelectedPayments(eligibleIds);
    } else {
      setSelectedPayments([]);
    }
  };

  const handleAddCheckClick = (payment: CheckRegisterItem) => {
    setSelectedPaymentForAddCheck(payment);
    setIsAddCheckModalOpen(true);
  };

  const handleAddCheckSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleBulkStatusUpdate = (status: WorkOrderPaymentStatus.CASHED | WorkOrderPaymentStatus.VOID) => {
    if (selectedPayments.length === 0) {
      toast({
        description: 'Please select at least one check',
        duration: 3000,
      });
      return;
    }
    updateStatusMutation.mutate(
      {
        paymentIds: selectedPayments,
        status,
      },
      {
        onSuccess: () => {
          toast({
            description: 'Payment Status Updated Successfully',
            duration: 3000,
          });
          setSelectedPayments([]);
          setSelectAll(false);
          setRefreshKey((prev) => prev + 1);
        },
        onError: (error) => {
          displayError(error.response?.data);
        },
      }
    );
  };

  const secondaryTableData = useMemo(() => {
    return tableData?.data?.map((d) => ({
      id: d.paymentId,
      data: [d],
    }));
  }, [tableData]);

  useEffect(() => {
    if (tableData?.rest) {
      const response = tableData.rest as {
        totalAmount?: string;
        totalChecks?: number;
      };
      setSummaryData({
        totalAmount: response.totalAmount,
        totalChecks: response.totalChecks,
      });
    }
  }, [tableData]);

  const searchConfig: DataTableSearchConfig[] = useMemo(
    () => [
      {
        name: 'clientIds',
        label: 'Search by Client(s)',
        placeholder: 'Search Client',
        fieldType: 'MULTISELECT' as const,
        multiSelectOptions: clientOptions,
        labelToDisplay: { dropdown: 'label1' as const, selectedOption: 'label2' as const },
      },
      {
        name: 'fromDate',
        label: 'From Date',
        placeholder: 'Select Date',
        fieldType: 'DATE' as const,
        disabledAt: (date: Date) => date > new Date(),
      },
      {
        name: 'toDate',
        label: 'To Date',
        placeholder: 'Select Date',
        fieldType: 'DATE' as const,
        disabledAt: (date: Date) => date > new Date(),
        dependsOn: {
          field: 'fromDate',
          resetOnChange: true,
          disableBefore: true,
        },
      },
      {
        name: 'invoiceNumbers',
        placeholder: 'Search Invoice Number',
        label: 'Search by Invoice Number(s)',
        fieldType: 'MULTISELECT' as const,
        multiSelectOptions: invoiceOptions,
        labelToDisplay: { dropdown: 'label1' as const, selectedOption: 'label2' as const },
      },
      {
        name: 'checkNumbers',
        placeholder: 'Search Check Number',
        label: 'Search by Check Number(s)',
        fieldType: 'MULTISELECT' as const,
        multiSelectOptions: checkNumberOptions,
        labelToDisplay: { dropdown: 'label1' as const, selectedOption: 'label2' as const },
      },
      {
        name: 'fileNumbers',
        placeholder: 'Search File Number',
        label: 'Search by File Number(s)',
        fieldType: 'MULTISELECT' as const,
        multiSelectOptions: fileNumberOptions,
        labelToDisplay: { dropdown: 'label1' as const, selectedOption: 'label2' as const },
      },
    ],
    [clientOptions, invoiceOptions, checkNumberOptions, fileNumberOptions]
  );

  const viewFilters = {
    buttonLabel: 'View',
    groups: [
      {
        label: 'Check Type',
        name: 'checkType',
        options: [
          { label: 'Witness Fee', value: 'WITNESS_FEE' },
          { label: 'Operating', value: 'OPERATING_CHECK' },
        ],
      },
      {
        label: 'Check Status',
        name: 'checkStatus',
        options: [
          { label: 'Issued', value: WorkOrderPaymentStatus.BILLED },
          { label: 'Cashed', value: WorkOrderPaymentStatus.CASHED },
          { label: 'Requested', value: WorkOrderPaymentStatus.REQUESTED },
          { label: 'Void', value: WorkOrderPaymentStatus.VOID },
        ],
      },
      {
        label: 'Duration',
        name: 'duration',
        options: [
          { label: '30 Days', value: '30_DAYS' },
          { label: '1 Year', value: '1_YEAR' },
        ],
      },
    ],
  };

  const getCheckDetailsColumns = (): ColumnDef<CheckRegisterItem>[] => [
    {
      accessorKey: 'details',
      header: '',
      size: 100,
      cell: (cell) => {
        const row = cell.row.original;
        return (
          <div className="flex gap-4 justify-between">
            <div className="flex flex-row w-[25%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Reference:</span>
              <span className="break-words flex-1">{row.reference ?? '-'}</span>
            </div>
            <div className="flex flex-row w-[25%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Case Name:</span>
              <span className="break-words flex-1">{row.caseName ?? '-'}</span>
            </div>
            <div className="flex flex-row w-[25%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Person to Serve:</span>
              <span className="break-words flex-1">{row.personToServe ?? '-'}</span>
            </div>
            <div className="flex flex-row w-[25%] gap-2 items-start">
              <span className="font-medium whitespace-nowrap flex-shrink-0">Issued By:</span>
              <span className="break-words flex-1">{row.issuedByName ?? '-'}</span>
            </div>
          </div>
        );
      },
    },
  ];

  const columns: ColumnDef<CheckRegisterItem>[] = useMemo(() => {
    return [
      {
        accessorKey: 'select',
        header: () => (
          <Checkbox
            id="select-all"
            checked={selectAll}
            onCheckedChange={(checked) => handleSelectAllChange(checked as boolean)}
          />
        ),
        size: 50,
        cell: ({ row }) => {
          const payment = row.original;
          const isEligible =
            payment.status === WorkOrderPaymentStatus.BILLED || payment.status === WorkOrderPaymentStatus.REQUESTED;
          const isChecked = selectedPayments.includes(payment.paymentId);
          return (
            <div className="flex w-full min-w-[40px]">
              <Checkbox
                id={`check-${payment.paymentId}`}
                checked={isChecked}
                disabled={!isEligible}
                onCheckedChange={(checked) => handleCheckboxChange(payment.paymentId, checked)}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'workOrderNumber',
        header: 'W/O #',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[80px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'issueDate',
        header: 'Issued Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.row.original.issuedDate;
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{value ? convertISODateToMMDDYYYY(value) : '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'clientId',
        header: 'Client #',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[80px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'checkNumber',
        header: 'Check #',
        enableSorting: true,
        cell: ({ row }) => {
          const payment = row.original;
          if (!payment.checkNumber && hasCheckUpdateFeature) {
            return (
              <div className="flex w-full min-w-[100px] mr-5">
                <span
                  className="text-[#1551B2] cursor-pointer hover:underline text-xs"
                  onClick={() => handleAddCheckClick(payment)}
                >
                  Add Check
                </span>
              </div>
            );
          }
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{payment.checkNumber ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'issuedTo',
        header: 'Issued To',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[120px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as number;
          return (
            <div className="flex w-full min-w-[80px] mr-5">
              <span>{formatCurrency(value)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ cell }) => {
          const status = cell.getValue() as WorkOrderPaymentStatus;
          const badgeConfig = statusBadges[status];
          if (!badgeConfig) return null;
          return (
            <Badge
              className={`mr-5 ${badgeClasses}`}
              style={{
                backgroundColor: badgeConfig.bgColor,
                color: badgeConfig.fontColor,
              }}
            >
              {badgeConfig.label}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'expand',
        header: '',
        cell: ({ row }) => {
          const rowId = row.original.paymentId;
          const isExpanded = openSecondaryColumns.find((col) => col.id === rowId);
          return (
            <div
              className="flex items-center flex-row min-w-[40px] justify-center cursor-pointer"
              onClick={() => {
                if (isExpanded) {
                  setOpenSecondaryColumns((prev) => prev.filter((col) => col.id !== rowId));
                } else {
                  setOpenSecondaryColumns((prev) => [...prev, { id: rowId, isExpanded: true }]);
                }
              }}
            >
              {isExpanded ? (
                <ChevronUp color="#676767" className="h-4 w-4" />
              ) : (
                <ChevronDown color="#676767" className="h-4 w-4" />
              )}
            </div>
          );
        },
        size: 75,
      },
    ];
  }, [tableData, selectedPayments, selectAll, openSecondaryColumns]);

  const summarySection = useMemo(
    () => (
      <div className="w-full flex justify-between flex-col md:flex-row gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Amount: </span>
            <span className="text-[#551175] text-sm font-[500]">
              {summaryData.totalAmount ? formatCurrency(parseFloat(summaryData.totalAmount)) : '$0.00'}
            </span>
          </div>
          <div className="flex flex-row gap-1">
            <span className="font-[500] text-sm">Total Checks: </span>
            <span className="text-[#551175] text-sm font-[500]">{summaryData.totalChecks ?? 0}</span>
          </div>
        </div>

        {hasBulkUpdatePermission && (
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  Bulk Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleBulkStatusUpdate(WorkOrderPaymentStatus.CASHED)}
                  disabled={updateStatusMutation.isPending}
                >
                  Mark as Cashed
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkStatusUpdate(WorkOrderPaymentStatus.VOID)}
                  disabled={updateStatusMutation.isPending}
                >
                  Mark as Void
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    ),
    [summaryData, selectedPayments, updateStatusMutation.isPending]
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <DataTable
        toolbar={{
          toolBarTitle: 'Check Register',
          filters: viewFilters,
        }}
        columns={columns}
        apiGet={apiGetWrapper}
        setRows={(data, rest) => setTableData({ data: data as CheckRegisterItem[], rest })}
        openSecondaryColumns={openSecondaryColumns}
        secondaryColumns={getCheckDetailsColumns}
        secondaryData={secondaryTableData}
        hideSecondaryHeaderRow
        search={searchConfig}
        summarySection={summarySection}
        getSecondaryTableRowId={(row) => row.paymentId}
        key={refreshKey}
      />

      <AddCheckNumberModal
        open={isAddCheckModalOpen}
        onOpenChange={setIsAddCheckModalOpen}
        payment={selectedPaymentForAddCheck}
        onSuccess={handleAddCheckSuccess}
        key={refreshKey}
      />
    </div>
  );
};

export default CheckRegister;
